import React, { useCallback } from 'react';
import { useTranslation } from 'localization';
import { UseCaseRow } from 'types/schema';

export interface UseCaseTagPickerMoleculeProps {
  selectedTags: string[];
  onChange: (tags: string[]) => void;
  useCases: UseCaseRow[];
  useCasesLoading?: boolean;
  useCasesError?: string | null;
  className?: string;
}

const UseCaseTagPickerMolecule: React.FC<UseCaseTagPickerMoleculeProps> = ({
  selectedTags,
  onChange,
  useCases,
  useCasesLoading = false,
  useCasesError = null,
  className = '',
}) => {
  const { t } = useTranslation();

  const handleToggle = useCallback(
    (systemName: string) => {
      const isSelected = selectedTags.includes(systemName);
      if (isSelected) {
        onChange(selectedTags.filter((tag) => tag !== systemName));
      } else {
        onChange([...selectedTags, systemName]);
      }
    },
    [selectedTags, onChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, systemName: string) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleToggle(systemName);
      }
    },
    [handleToggle],
  );

  const classes = [
    'marketplace-usecase-picker',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Only show leaf use cases (no children)
  const parentNames = new Set(useCases.map((uc) => uc.parent_system_name).filter(Boolean));
  const leafUseCases = useCases.filter((uc) => !parentNames.has(uc.system_name));

  const formatLabel = (uc: UseCaseRow): string => {
    if (uc.name) return uc.name;
    return uc.system_name
      .split(/[-_]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className={classes}>
      <label className="marketplace-usecase-picker__label">
        {t('marketplace.useCases.selectLabel')}
      </label>

      {useCasesLoading && (
        <div className="marketplace-usecase-picker__loading">
          <span className="marketplace-marketplace__spinner" />
          {t('marketplace.common.loading')}
        </div>
      )}

      {useCasesError && (
        <div className="marketplace-usecase-picker__error">
          {t('marketplace.useCases.loadError')}
        </div>
      )}

      {!useCasesLoading && !useCasesError && leafUseCases.length === 0 && (
        <div className="marketplace-usecase-picker__empty">
          {t('marketplace.useCases.noUseCases')}
        </div>
      )}

      {!useCasesLoading && leafUseCases.length > 0 && (
        <div className="marketplace-usecase-picker__options" role="group" aria-label={t('marketplace.useCases.selectLabel')}>
          {leafUseCases.map((uc) => {
            const isSelected = selectedTags.includes(uc.system_name);
            const pillClasses = [
              'marketplace-usecase-picker__pill',
              isSelected ? 'marketplace-usecase-picker__pill--selected' : '',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <span
                key={uc.system_name}
                className={pillClasses}
                role="checkbox"
                aria-checked={isSelected}
                tabIndex={0}
                onClick={() => handleToggle(uc.system_name)}
                onKeyDown={(e) => handleKeyDown(e, uc.system_name)}
              >
                {uc.icon && (
                  <i
                    className={`pi pi-${uc.icon.replace(/^pi[- ]/, '')} marketplace-usecase-picker__icon`}
                    aria-hidden="true"
                  />
                )}
                {formatLabel(uc)}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UseCaseTagPickerMolecule;
